import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
    useGetReleaseNotesQuery,
    useMarkReleaseNotesSeenMutation,
} from '../../redux/commonApiCalls'
import PageTitle from '../../components/PageTitle'

const sxHeroBanner = {
    background: 'linear-gradient(135deg, #5b67d8 0%, #6b46c1 100%)',
    borderRadius: 3,
    p: 4,
    mb: 3,
    color: 'common.white',
    position: 'relative',
    overflow: 'hidden',
}

const sxHeroIcon = {
    width: 56,
    height: 56,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const sxFilterCard = {
    p: 2,
    mb: 2,
    borderRadius: 2,
    backgroundColor: '#fff',
    border: '1px solid',
    borderColor: 'grey.200',
}

const sxNoteCard = {
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'grey.200',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'linear-gradient(90deg, #5b67d8 0%, #06b6d4 100%)',
    },
    '&:hover': {
        borderColor: '#5b67d8',
        boxShadow: '0 4px 20px rgba(91,103,216,0.15)',
    },
}

const TYPE_COLOR = {
    feature: { bg: '#eef2ff', fg: '#4f46e5' },
    fix: { bg: '#fef3c7', fg: '#b45309' },
    improvement: { bg: '#dcfce7', fg: '#15803d' },
    breaking: { bg: '#fee2e2', fg: '#b91c1c' },
}

const formatDate = (val) => {
    if (!val) return ''
    const d = new Date(val)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const getChangeType = (note) => {
    const type = (note?.changeType || note?.type || note?.changes?.[0]?.type || '').toString()
    return type
}

const normalizeType = (t) => (t || '').toLowerCase()

const getTypeChip = (type) => {
    const key = normalizeType(type)
    const color = TYPE_COLOR[key] || { bg: '#e0e7ff', fg: '#4338ca' }
    return (
        <Chip
            label={type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Update'}
            size='small'
            sx={{
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                backgroundColor: color.bg,
                color: color.fg,
                '& .MuiChip-label': { px: 1.2 },
            }}
        />
    )
}

export default function ReleaseNotes() {
    const navigate = useNavigate()

    const [search, setSearch] = useState('')
    const [dateFilter, setDateFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('ALL')

    // RTK Query — cached + deduped. Fires once per session unless data goes stale.
    const { data, isFetching, error: queryError } = useGetReleaseNotesQuery({ limit: 50 })
    const [markSeen] = useMarkReleaseNotesSeenMutation()

    const notes = data?.items || []
    const loading = isFetching && notes.length === 0
    const error = queryError ? (queryError?.data?.message || 'Failed to load release notes') : null

    // Mark seen exactly once per mount — invalidates the unread-count cache tag.
    useEffect(() => { markSeen() }, [markSeen])

    const availableTypes = useMemo(() => {
        const set = new Set()
        notes.forEach((n) => {
            const t = getChangeType(n)
            if (t) set.add(t.toString())
        })
        return Array.from(set)
    }, [notes])

    const filtered = useMemo(() => {
        return notes.filter((n) => {
            if (search) {
                const q = search.toLowerCase()
                const t = (n.title || n.headline || '').toLowerCase()
                const s = (n.summary || n.description || '').toLowerCase()
                const v = (n.version || n.id || '').toString().toLowerCase()
                if (!t.includes(q) && !s.includes(q) && !v.includes(q)) return false
            }
            if (typeFilter !== 'ALL') {
                if (normalizeType(getChangeType(n)) !== normalizeType(typeFilter)) return false
            }
            if (dateFilter) {
                const d = n.releaseDate || n.publishedAt || n.createdAt
                if (!d) return false
                const noteDate = new Date(d).toISOString().slice(0, 10)
                if (noteDate !== dateFilter) return false
            }
            return true
        })
    }, [notes, search, typeFilter, dateFilter])

    const featureCount = notes.filter((n) => normalizeType(getChangeType(n)) === 'feature').length
    const fixCount = notes.filter((n) => normalizeType(getChangeType(n)) === 'fix').length
    const improvementCount = notes.filter((n) => normalizeType(getChangeType(n)) === 'improvement').length

    const goToDetail = (note) => {
        const id = note.id || note._id || note.shortCode
        navigate(`/whats-new/${encodeURIComponent(id)}`, { state: { note } })
    }

    return (
        <>
            <PageTitle>What's New</PageTitle>

            <Box sx={sxHeroBanner}>
                <Stack direction='row' spacing={2.5} alignItems='center'>
                    <Box sx={sxHeroIcon}>
                        <CampaignOutlinedIcon sx={{ fontSize: 30, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant='h5' fontWeight={700} sx={{ mb: 0.5 }}>
                            What's New in ProShip
                        </Typography>
                        <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            {notes.length} {notes.length === 1 ? 'release' : 'releases'} — features, fixes & improvements
                        </Typography>
                        <Stack direction='row' spacing={1} sx={{ mt: 1.5 }}>
                            {featureCount > 0 && (
                                <Chip
                                    label={`${featureCount} ${featureCount === 1 ? 'Feature' : 'Features'}`}
                                    size='small'
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
                                />
                            )}
                            {fixCount > 0 && (
                                <Chip
                                    label={`${fixCount} ${fixCount === 1 ? 'Fix' : 'Fixes'}`}
                                    size='small'
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
                                />
                            )}
                            {improvementCount > 0 && (
                                <Chip
                                    label={`${improvementCount} ${improvementCount === 1 ? 'Improvement' : 'Improvements'}`}
                                    size='small'
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
                                />
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            <Box sx={sxFilterCard}>
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            size='small'
                            placeholder='Search by title, summary or version...'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon fontSize='small' />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size='small'
                            type='date'
                            label='Date'
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 2 }}>
                    <FilterListIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Type
                    </Typography>
                    <Chip
                        label='All'
                        size='small'
                        clickable
                        onClick={() => setTypeFilter('ALL')}
                        variant={typeFilter === 'ALL' ? 'filled' : 'outlined'}
                        sx={{
                            fontWeight: 600,
                            ...(typeFilter === 'ALL' && {
                                backgroundColor: '#eef2ff',
                                color: '#4f46e5',
                                border: '1px solid #4f46e5',
                            }),
                        }}
                    />
                    {availableTypes.map((t) => {
                        const count = notes.filter((n) => normalizeType(getChangeType(n)) === normalizeType(t)).length
                        const selected = normalizeType(typeFilter) === normalizeType(t)
                        const color = TYPE_COLOR[normalizeType(t)] || { bg: '#e0e7ff', fg: '#4338ca' }
                        return (
                            <Chip
                                key={t}
                                clickable
                                size='small'
                                onClick={() => setTypeFilter(t)}
                                label={
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color.fg }} />
                                        {t.charAt(0).toUpperCase() + t.slice(1)} {count}
                                    </span>
                                }
                                variant={selected ? 'filled' : 'outlined'}
                                sx={{
                                    fontWeight: 600,
                                    ...(selected && {
                                        backgroundColor: color.bg,
                                        color: color.fg,
                                        border: `1px solid ${color.fg}`,
                                    }),
                                }}
                            />
                        )
                    })}
                </Stack>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : error ? (
                <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
                    <Typography>{error}</Typography>
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center', backgroundColor: '#fff', borderRadius: 2, border: '1px dashed', borderColor: 'grey.300' }}>
                    <Typography variant='body1' color='text.secondary'>No release notes found</Typography>
                </Box>
            ) : (
                <Stack spacing={2}>
                    {filtered.map((note) => {
                        const id = note.id || note._id || note.shortCode
                        const date = note.releaseDate || note.publishedAt || note.createdAt
                        const author = note.publishedBy || note.createdBy || note.author
                        const changesCount = Array.isArray(note.changes) ? note.changes.length : (note.changesCount || 1)
                        const isNew = note.isNew ?? note.unread ?? false
                        return (
                            <Card key={id} sx={sxNoteCard} elevation={0}>
                                <CardActionArea onClick={() => goToDetail(note)} sx={{ p: 2.5 }}>
                                    <Grid container alignItems='flex-start' spacing={2}>
                                        <Grid item xs={12} md={9}>
                                            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1, flexWrap: 'wrap' }}>
                                                {note.version && (
                                                    <Chip
                                                        label={note.version}
                                                        size='small'
                                                        variant='outlined'
                                                        sx={{ fontFamily: 'monospace', fontSize: 11, height: 22 }}
                                                    />
                                                )}
                                                {getTypeChip(getChangeType(note))}
                                                {note.highlight && (
                                                    <Chip
                                                        label='HIGHLIGHT'
                                                        size='small'
                                                        sx={{
                                                            backgroundColor: '#fef3c7',
                                                            color: '#b45309',
                                                            fontWeight: 700,
                                                            fontSize: 10,
                                                            height: 20,
                                                        }}
                                                    />
                                                )}
                                                {isNew && (
                                                    <Chip
                                                        label='NEW'
                                                        size='small'
                                                        sx={{
                                                            backgroundColor: '#e0e7ff',
                                                            color: '#4338ca',
                                                            fontWeight: 700,
                                                            fontSize: 10,
                                                            height: 20,
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                            <Typography variant='subtitle1' fontWeight={700} color='text.primary' sx={{ mb: 0.5 }}>
                                                {note.title || note.headline || '(untitled release)'}
                                            </Typography>
                                            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                                {note.summary || note.description || ''}
                                            </Typography>
                                            <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mt: 1, color: 'text.disabled' }}>
                                                <Typography variant='caption'>{formatDate(date)}</Typography>
                                                {author && (
                                                    <>
                                                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: 'grey.400' }} />
                                                        <Typography variant='caption'>{author}</Typography>
                                                    </>
                                                )}
                                                <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: 'grey.400' }} />
                                                <Typography variant='caption' sx={{ color: '#4f46e5', fontWeight: 600 }}>
                                                    {changesCount} {changesCount === 1 ? 'change' : 'changes'}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                            <Stack direction={{ xs: 'row', md: 'column' }} alignItems={{ xs: 'center', md: 'flex-end' }} spacing={1}>
                                                <Chip
                                                    label={formatDate(date)}
                                                    size='small'
                                                    sx={{ backgroundColor: '#f3f4f6', fontWeight: 600, fontSize: 11 }}
                                                />
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', border: '1px solid', borderColor: 'grey.300' }}>
                                                    <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardActionArea>
                            </Card>
                        )
                    })}
                </Stack>
            )}
        </>
    )
}
